import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FolderTree, ArrowRight, CheckCircle2, SkipForward, RefreshCw, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Iter-157: step-by-step Drive migration wizard for legacy clients that don't
 * yet have a Shared Drive folder linked. The CEO walks through them one by one,
 * pasting the Drive URL for each. On submit, the backend:
 *   1. Validates + parses the Drive ID
 *   2. Sets `drive_folder_id` + flips status → ACTIVE
 *   3. Auto-creates 001-Devis / 002-BDC / 003-CRA subfolders
 *   4. Provisions per-mission subfolders inside 002-BDC and 003-CRA for every
 *      mission attached to the client.
 */
export default function DriveMigrationWizard() {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(false);
  const [idx, setIdx] = useState(0);
  const [url, setUrl] = useState("");
  const [folderName, setFolderName] = useState("");
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState({ migrated: 0, missions: 0, skipped: 0 });
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/drive-migration/pending");
      setPending(r.data || []);
      setIdx(0);
      setUrl(""); setFolderName("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec chargement");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); }, [open]);

  // Iter-193 — let the Drive health alert card trigger the wizard remotely so the
  // "Clients without Drive" KPI becomes directly actionable.
  // Iter-194o — accept an optional `detail.client_id` so the popup can preselect
  // a specific client when opening the wizard.
  const [preselectClientId, setPreselectClientId] = useState(null);
  useEffect(() => {
    const onOpen = (ev) => {
      setOpen(true);
      const cid = ev?.detail?.client_id || null;
      if (cid) setPreselectClientId(cid);
    };
    window.addEventListener("drive-migration-open", onOpen);
    return () => window.removeEventListener("drive-migration-open", onOpen);
  }, []);

  // When the pending list (re)loads, if a preselect was requested, jump to it.
  useEffect(() => {
    if (!preselectClientId || pending.length === 0) return;
    const i = pending.findIndex((c) => c.client_id === preselectClientId);
    if (i >= 0) setIdx(i);
    setPreselectClientId(null);
  }, [preselectClientId, pending]);

  const cur = pending[idx];

  // Iter-194h — when the next pending row already has a Drive folder linked
  // but the subtree is missing (reason=INCOMPLETE_SUBTREE), pre-fill the URL
  // input so the CEO just has to click "Migrer" to re-provision the tree.
  useEffect(() => {
    if (!cur) return;
    if (cur.reason === "INCOMPLETE_SUBTREE" && cur.drive_folder_url) {
      setUrl(cur.drive_folder_url);
    } else {
      setUrl("");
    }
    setFolderName("");
  }, [cur?.client_id]);

  const submit = async () => {
    if (!cur) return;
    if (!url.trim()) { toast.error("Lien Drive requis"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/admin/drive-migration/${cur.client_id}`, {
        drive_folder_url: url.trim(),
        drive_folder_name: folderName.trim() || undefined,
      });
      toast.success(`${cur.name} migré (${r.data.missions_provisioned} mission(s))`);
      setStats((s) => ({ ...s, migrated: s.migrated + 1, missions: s.missions + (r.data.missions_provisioned || 0) }));
      // Move to next; remove the migrated one from the local list
      const remaining = pending.filter((c, i) => i !== idx);
      setPending(remaining);
      setIdx(Math.min(idx, remaining.length - 1));
      setUrl(""); setFolderName("");
      // Iter-193 — notify DriveHealthBlock so the "Clients without Drive" KPI count
      // refreshes immediately (was a stale `1` after the wizard had cleared the
      // backlog in production).
      window.dispatchEvent(new CustomEvent("drive-health-changed"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Migration échouée");
    } finally { setBusy(false); }
  };

  const skip = () => {
    if (!cur) return;
    setStats((s) => ({ ...s, skipped: s.skipped + 1 }));
    if (idx < pending.length - 1) {
      setIdx(idx + 1);
    } else {
      setIdx(Math.min(idx, pending.length - 2));
    }
    setUrl(""); setFolderName("");
  };

  if (!open) {
    return (
      <div className="bg-white border border-zinc-200 rounded-lg p-6 mb-6" data-testid="drive-migration-card">
        <div className="flex items-center gap-3 text-zinc-900 mb-2">
          <FolderTree size={18} className="text-zinc-700" />
          <h2 className="font-heading font-bold text-lg">Drive · Assainissement legacy</h2>
        </div>
        <p className="text-xs text-zinc-500 max-w-2xl leading-relaxed mb-4">
          Wizard pas-à-pas pour rattraper les clients existants sans dossier Google Drive.
          Pour chaque client, vous collez le lien du Drive partagé — l'application
          crée automatiquement les sous-dossiers <code>001-Devis</code>, <code>002-BDC</code>,
          <code>003-CRA</code> ainsi que les sous-dossiers par mission dans
          <code>002-BDC/&lt;mission&gt;/</code> et <code>003-CRA/&lt;mission&gt;/</code>.
        </p>
        <button
          onClick={() => setOpen(true)}
          data-testid="drive-migration-start-btn"
          className="inline-flex items-center gap-2 px-4 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800"
        >
          <FolderTree size={14} /> Démarrer l'assainissement
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white border border-zinc-300 rounded-lg p-6 mb-6 shadow-sm" data-testid="drive-migration-wizard">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3 text-zinc-900">
          <FolderTree size={18} className="text-zinc-700" />
          <h2 className="font-heading font-bold text-lg">Assainissement Drive — étape {pending.length === 0 ? "—" : `${idx + 1}/${pending.length}`}</h2>
        </div>
        <button onClick={() => setOpen(false)} data-testid="drive-migration-close" className="p-1.5 hover:bg-zinc-100 rounded-md">
          <X size={18} />
        </button>
      </div>

      {loading && <div className="text-sm text-zinc-500 py-8 text-center">Chargement…</div>}

      {!loading && pending.length === 0 && (
        <div className="text-center py-8 space-y-3">
          <CheckCircle2 size={36} className="mx-auto text-emerald-600" />
          <p className="text-sm text-zinc-700">Aucun client à migrer — tous les clients ont un Drive lié.</p>
          <p className="text-xs text-zinc-500">Migrés cette session : {stats.migrated} · Skip : {stats.skipped} · Missions provisionnées : {stats.missions}</p>
          <button onClick={load} data-testid="drive-migration-refresh" className="inline-flex items-center gap-1.5 px-3 h-9 border border-zinc-200 text-xs rounded-md hover:bg-zinc-50">
            <RefreshCw size={12} /> Re-scanner
          </button>
        </div>
      )}

      {!loading && cur && (
        <div className="space-y-4">
          <div className="bg-zinc-50 border border-zinc-200 rounded-md p-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">Client à migrer</div>
            <div className="font-semibold text-base text-zinc-900">{cur.name}</div>
            <div className="text-xs text-zinc-500 mt-1">
              <span className={`inline-block mr-2 px-1.5 py-0.5 rounded text-[10px] font-mono ${cur.status === "PENDING_VALIDATION" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
                {cur.status}
              </span>
              {cur.country && <span className="mr-2">{cur.country}</span>}
              {cur.is_subcontractor && <span className="mr-2 italic">(sous-traitant)</span>}
              <span>{cur.missions_count} mission{cur.missions_count > 1 ? "s" : ""} associée{cur.missions_count > 1 ? "s" : ""}</span>
            </div>
            {/* Iter-194h — show the precise reason so the CEO immediately
                understands whether (a) no Drive folder was ever linked, or
                (b) the folder is linked but the 001/002/003 subtree is
                missing (e.g. after the folder ID was changed). */}
            {cur.reason === "INCOMPLETE_SUBTREE" && (
              <div className="mt-2 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded px-2 py-1.5">
                <b>Arborescence incomplète :</b> Drive lié mais 001-Devis / 002-BDC / 003-CRA non provisionnés.
                Soumettre la même URL re-créera le sous-arbre.
                {cur.drive_folder_url && (
                  <div className="mt-1">
                    <a href={cur.drive_folder_url} target="_blank" rel="noreferrer"
                       className="font-mono text-[10px] underline break-all">
                      {cur.drive_folder_url}
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>Lien Drive partagé <span className="text-red-600">*</span></Label>
            <Input
              data-testid="drive-migration-url-input"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://drive.google.com/drive/folders/..."
              className="font-mono text-xs"
              autoFocus
            />
            <Label>Nom du dossier (optionnel — affiché dans la fiche client)</Label>
            <Input
              data-testid="drive-migration-name-input"
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              placeholder={`Drive ${cur.name}`}
            />
          </div>

          <div className="flex items-center gap-2 text-xs text-zinc-500 italic">
            <span>L'application va automatiquement :</span>
            <span>① vérifier l'accès</span>
            <ArrowRight size={11} />
            <span>② créer 001/002/003</span>
            <ArrowRight size={11} />
            <span>③ provisionner {cur.missions_count} sous-dossier(s) mission</span>
          </div>

          <div className="flex items-center justify-between border-t border-zinc-100 pt-4">
            <button
              onClick={skip}
              data-testid="drive-migration-skip"
              className="inline-flex items-center gap-1.5 px-3 h-9 border border-zinc-200 text-xs rounded-md hover:bg-zinc-50"
            >
              <SkipForward size={12} /> Passer ce client
            </button>
            <button
              onClick={submit}
              disabled={busy || !url.trim()}
              data-testid="drive-migration-apply"
              className="inline-flex items-center gap-2 px-4 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800 disabled:opacity-50"
            >
              {busy ? "Migration…" : <>Migrer & continuer <ArrowRight size={14} /></>}
            </button>
          </div>

          <div className="text-[11px] text-zinc-400 text-right">
            Migrés cette session : {stats.migrated} · Skip : {stats.skipped} · Missions provisionnées : {stats.missions}
          </div>
        </div>
      )}
    </div>
  );
}
